import {
  BoxV2 as Box,
  Button,
  DropdownV2 as Dropdown,
  Flex,
  TextInput,
} from 'portal-commons';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { faSquareMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useControllers } from './hooks';
import { SuspendFormValues } from './types';
import { CampaignDetail } from '@/apis/core';
import Modal from '@/components/ModalV2';
import { SelectItem } from '@/utils/types';

const DefaultFormValues: SuspendFormValues = {
  suspensionCategory: '',
  explanation: '',
};

interface SuspendModalProps {
  open?: boolean;
  disabled?: boolean;
  campaign: CampaignDetail;
  suspensionCategoryOptions: SelectItem[];
  onClose?: () => void;
  onSubmit?: (suspensionCategory: string, explanation: string) => void;
}

const SuspendModal: FC<SuspendModalProps> = ({
  open,
  disabled,
  campaign,
  suspensionCategoryOptions,
  onClose,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<SuspendFormValues>({
    defaultValues: DefaultFormValues,
  });
  const {
    suspensionCategory,
    setSuspensionCategory,
    suspensionCategoryError,
    explanation,
    setExplanation,
    validateExplanation,
    explanationError,
  } = useControllers(control);

  const handleCancel = () => {
    onClose?.();
  };

  const handleValid = (value: SuspendFormValues) => {
    onSubmit?.(value.suspensionCategory, value.explanation);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal data-testid="suspendModal" open={open} sx={{ padding: '24px 32px' }}>
      <form
        style={{ width: '100%' }}
        noValidate
        onSubmit={handleSubmit(handleValid)}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'xl',
            color: 't.black70',
            fontWeight: 400,
            lineHeight: 'normal',
          }}
        >
          <Flex
            sx={{
              alignItems: 'center',
              gap: 's',
            }}
          >
            <Flex
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                color: 't.black80',
                fontSize: '22px',
              }}
            >
              <FontAwesomeIcon icon={faSquareMinus} />
            </Flex>
            <Box sx={{ color: 't.black90', fontWeight: 600, fontSize: 'H600' }}>
              Suspend Campaign
            </Box>
          </Flex>

          <Flex sx={{ flexDirection: 'column', gap: 'm', width: '100%' }}>
            <Box sx={{ fontSize: 'H100' }}>* Indicates a Required Field</Box>
            <TextInput
              data-testid="suspendModalCampaignUid"
              disabled
              label="Campaign ID"
              value={campaign.uid}
            />
            <TextInput
              data-testid="suspendModalBrandName"
              disabled
              label="Brand DBA Name"
              value={campaign.brandName}
            />
            <Dropdown
              data-testid="suspendModalSuspensionCategory"
              required
              disabled={disabled}
              error={!!suspensionCategoryError?.message}
              label="Campaign Suspension Category"
              helperText={suspensionCategoryError?.message}
              options={suspensionCategoryOptions}
              value={suspensionCategory}
              onChange={setSuspensionCategory}
            />
            <TextInput
              data-testid="suspendModalExplanation"
              required
              disabled={disabled}
              error={!!explanationError?.message}
              label="Explanation"
              note={explanationError?.message}
              value={explanation}
              onChange={setExplanation}
              onBlur={validateExplanation}
            />
          </Flex>

          <Flex sx={{ gap: '28px' }}>
            <Button
              data-testid="suspendModalCancelButton"
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              data-testid="suspendModalSubmitButton"
              type="submit"
              disabled={
                disabled ||
                Object.values(errors).some((value) => !!value.message)
              }
            >
              Suspend
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default SuspendModal;
